<template>
  <q-layout
    id="kds-layout"
    view="hHh LpR fFf"
    :container="container"
    style="touch-action: none"
  >
    <q-header
      v-if="showSummaryPage"
      class="full-width bg-grey-8 q-mb-xs"
    >
      <q-btn
        class="full-width"
        no-wrap
        square
        flat
        dense
        ripple
        @click="showSummaryPage = false"
      >
        <span class="full-width row justify-between">
          <q-icon name="none" />
          <span class="text-h7 text-uppercase text-white">{{ kitchenDisplay.name }} Summary</span>
          <q-icon name="close" />
        </span>
      </q-btn>
    </q-header>
    <kitchen-display-header
      v-else
      :kitchen-display="kitchenDisplay"
      :tab="tab"
      :loading="kitchenOrders.loading"
      @set-tab="value => tab = value"
      @show-settings="showSettingsDrawer = !showSettingsDrawer"
      @show-summary="showSummaryDrawer = !showSummaryDrawer"
      @refresh="refresh"
    >
      <template #summary-btn>
        <q-badge
          :label="summaryTotal"
          color="primary"
          :text-color="$isColorDark(currentSite.primaryColor) ? 'white' : 'dark'"
          rounded
          floating
        />
      </template>
    </kitchen-display-header>

    <kitchen-settings-drawer
      v-if="!showSummaryPage"
      v-model="showSettingsDrawer"
      :limit="limit"
      :locations-filter="locationsFilter"
      :locations="kitchenLocations"
      :delivery-types-filter="deliveryTypesFilter"
      :delivery-locations-filter="deliveryLocationsFilter"
      :prepare-by="prepareBy"
      :summarize-by="summarizeBy"
      :zoom="zoom"
      @set-kitchen-display="setKitchenDisplay"
      @set-limit="value => limit = value"
      @set-prepare-by="value => prepareBy = value"
      @set-summarize-by="value => summarizeBy = value"
      @set-zoom="value => zoom = value"
    />

    <kitchen-summary-drawer
      v-if="kitchenDisplay && !showSummaryPage"
      v-model="showSummaryDrawer"
      style="min-width: 320px; max-width: 420px;"
      :title="`${kitchenDisplay.name} Summary`"
      :summary-items="sortedSummaryItems"
      @show-summary-page="showSummaryPage = true"
    />

    <!-- MAIN DISPLAY -->
    <q-page-container>
      <kitchen-summary-page
        v-if="showSummaryPage"
        :summary-items="sortedSummaryItems"
        @close="showSummaryPage = false"
      />
      <kitchen-orders-page
        v-else-if="kitchenDisplay"
        :orders="kitchenOrders"
        :kitchen-display="kitchenDisplay"
        :prepare-by="prepareBy"
        :tab="tab"
        :zoom="zoom"
        @refresh="refresh"
      />
    </q-page-container>

    <!-- DIALOGS -->
    <set-kitchen-display-dialog
      v-if="showSetKitchenDisplayDialog"
      :model-value="showSetKitchenDisplayDialog"
      @site-changed="$mh.setThemeColors(currentSite)"
      @update:model-value="value => showSetKitchenDisplayDialog = value"
    />
  </q-layout>
</template>

<script>
import { Cookies } from 'quasar'
import { isElectronMode } from 'src/config'
import { DELIVERY_TYPE_OPTIONS } from 'src/models/delivery-type/DeliveryType'
import { GROUP_BY, STAGES } from 'src/models/kitchen-display/KitchenDisplayType'
import KitchenDisplayHeader from 'src/modules/kds/components/kitchen-display-header/KitchenDisplayHeader'
import KitchenSettingsDrawer from 'src/modules/kds/components/KitchenSettingsDrawer'
import KitchenSummaryDrawer from 'src/modules/kds/components/KitchenSummaryDrawer'
import SetKitchenDisplayDialog from 'src/modules/kds/dialogs/SetKitchenDisplayDialog'
import KitchenOrdersPage from 'src/modules/kds/pages/KitchenOrdersPage'
import KitchenSummaryPage from 'src/modules/kds/pages/KitchenSummaryPage'

export default {
  name: 'KdsLayout',
  components: {
    KitchenDisplayHeader,
    KitchenSettingsDrawer,
    KitchenSummaryDrawer,
    KitchenOrdersPage,
    KitchenSummaryPage,
    SetKitchenDisplayDialog
  },
  configuration: ['currentSite', 'deliveryLocations'],
  context: ['currentUser', 'currentKdsClient', 'currentRegisterClient'],
  provide () {
    return {
      kitchenDisplayType: this.kitchenDisplayType
    }
  },
  props: {
    registerKitchenDisplay: {
      type: Object,
      required: false,
      default: null
    },
    container: {
      type: Boolean,
      required: false,
      default: false
    },
    autoFetch: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  models: {
    kitchenOrders: {
      skipWatchers: true,
      orderItems: {
        skipWatchers: true,
        itemOrderItem: {
          item: 'default',
          itemVariation: 'default',
          itemModifierNames: true
        },
        pickPrepPrintedAt: true,
        packingRequired: true
      },
      user: {
        fields: ['id', 'firstName', 'lastName']
      },
      employee: {
        fields: ['id', 'firstName', 'lastName']
      },
      immediate: true,
      variables () {
        return {
          kitchenDisplayId: this.kitchenDisplay?.id,
          prepared: this.preparedFilterValue,
          delivered: this.deliveredFilterValue,
          locationIds: this.locationsFilterArray,
          deliveryTypes: this.deliveryTypesFilterArray,
          deliveryLocations: this.deliveryLocationsFilterArray,
          limit: this.limit,
          skip: !this.kitchenDisplay?.isRemote || !this.currentUser,
          noCount: true
        }
      }
    },
    kitchenOrderSummary: {
      variables () {
        return {
          kitchenDisplayId: this.kitchenDisplay?.id,
          prepared: this.preparedFilterValue,
          delivered: this.deliveredFilterValue,
          locationIds: this.locationsFilterArray,
          deliveryTypes: this.deliveryTypesFilterArray,
          deliveryLocations: this.deliveryLocationsFilterArray,
          summarizeBy: this.summarizeBy,
          skip: this.$register && !this.kitchenDisplay?.isRemote
        }
      },
      immediate: true
    }
  },
  data () {
    return {
      showSummaryPage: false,
      showSetKitchenDisplayDialog: !!this.currentUser && !this.currentKdsClient?.kitchenDisplay?.isRemote && !this.currentRegisterClient?.registerStation?.isRemote,
      showSettingsDrawer: false,
      showSummaryDrawer: false,
      tab: STAGES.OPEN,
      limit: 20,
      locationsFilter: {},
      deliveryTypesFilter: _(DELIVERY_TYPE_OPTIONS.KITCHEN).keyBy().mapValues(_ => false).value(),
      deliveryLocationsFilter: {},
      prepareBy: GROUP_BY.NONE,
      summarizeBy: GROUP_BY.VARIATION,
      zoom: null,
      electronPrinterConnected: false,
      electronPrinterStatusInterval: null,
      updateKdsDisplayInterval: null
    }
  },
  computed: {
    kitchenDisplay () {
      return this.registerKitchenDisplay || this.currentKdsClient?.kitchenDisplay
    },
    kitchenDisplayType () {
      return this.kitchenDisplay?.kitchenDisplayType
    },
    kitchenLocations () {
      return this.kitchenDisplay?.locations?.data || []
    },
    orderItems () {
      return _(this.kitchenOrders.data)
        .map(order => order.orderItems.data)
        .flatten()
        .filter(orderItem => !orderItem.isHeld && this.isOrderItemVisible(orderItem))
        .value()
    },
    sortedSummaryItems () {
      if (!this.kitchenOrderSummary?.value) return []

      return _.sortBy(this.kitchenOrderSummary?.value, summaryItem => -summaryItem.count)
    },
    summaryTotal () {
      return _.sumBy(this.sortedSummaryItems, 'count')
    },
    locationsFilterArray () {
      return this.$mh.objectToArray(this.locationsFilter)
    },
    deliveryTypesFilterArray () {
      return this.$mh.objectToArray(this.deliveryTypesFilter)
    },
    deliveryLocationsFilterArray () {
      return this.$mh.objectToArray(this.deliveryLocationsFilter)
    },
    preparedFilterValue () {
      if (_.includes(STAGES.PREPARED, this.tab)) {
        return true
      } else if (_.includes(STAGES.UNPREPARED, this.tab)) {
        return false
      } else {
        return null
      }
    },
    deliveredFilterValue () {
      if (_.includes(STAGES.DELIVERED, this.tab)) {
        return true
      } else if (_.includes(STAGES.UNDELIVERED, this.tab)) {
        return false
      } else {
        return null
      }
    }
  },
  watch: {
    'kitchenDisplay.id': {
      handler () {
        this.locationsFilter = _(this.kitchenDisplay?.locations?.data).map('id').keyBy().mapValues(_ => false).value() || this.locationsFilter
        this.deliveryLocationsFilter = _(this.deliveryLocations?.data).map('name').keyBy().mapValues(_ => false).value() || this.deliveryLocationsFilter
        this.prepareBy = this.kitchenDisplayType?.prepareBy || this.prepareBy
        this.summarizeBy = this.kitchenDisplayType?.summarizeBy || this.summarizeBy
        this.tab = _.first(this.kitchenDisplayType?.deliveryStages) || this.tab
      },
      immediate: true
    },
    autoFetch: {
      handler () {
        if (this.autoFetch) {
          this.setKitchenOrdersInterval()
        } else {
          this.clearKitchenOrdersInterval()
        }
      },
      immediate: true
    },
    zoom: {
      handler () {
        if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
          let viewport = document.querySelector('meta[name=viewport]')
          if (!viewport) {
            viewport = document.createElement('meta')
            viewport.name = 'viewport'
            const kitchenOrdersPageElement = document.getElementById('kitchen-orders-page')
            if (kitchenOrdersPageElement) kitchenOrdersPageElement.appendChild(viewport)
          }
          const width = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0)

          if (this.zoom === null) {
            if (width > 1300) {
              // iPad Pro
              this.zoom = 1.2
            } else if (width < 500) {
              this.zoom = 0.7
            } else if (width < 300) {
              this.zoom = 0.5
            } else {
              this.zoom = 1
            }
          }

          const content = `width=${width / this.zoom}, initial-scale=${this.zoom}, maximum-scale=${this.zoom}, user-scalable=no`
          viewport.setAttribute('content', content)
        } else {
          this.zoom ||= 1
          const kitchenOrdersPageElement = document.getElementById('kitchen-orders-page')
          if (kitchenOrdersPageElement) kitchenOrdersPageElement.style.zoom = this.zoom
        }
      },
      immediate: true
    },
    limit () {
      let cookie = Cookies.get(this.$constants.cookies.kdsCookies)
      cookie ||= {}
      cookie.limit = this.limit
      Cookies.set(this.$constants.cookies.kdsCookies, cookie, { expires: 365 })
    },
    electronPrinterConnected () {
      if (!this.$kds) return

      if (this.electronPrinterConnected && this.currentKdsClient && !this.currentKdsClient.isElectronMode) {
        this.updateKdsClientIsElectronMode(true)
      } else if (!this.electronPrinterConnected && this.currentKdsClient?.isElectronMode) {
        this.updateKdsClientIsElectronMode(false)
      }
    }
  },
  async mounted () {
    // TODO INDY-3560 Some of this (electron printer commands) might be better to not skip
    if (!this.$kds) return

    if (!this.currentUser && !this.currentKdsClient) {
      this.$router.push('/login')
      return
    }

    this.setThemeColors()

    if (!this.currentKdsClient || this.currentUser?.userCircuitMigratedUrl) {
      this.showSetKitchenDisplayDialog = true
    } else {
      isElectronMode && !window.electronPrinter.status()?.error && this.updateKdsClientIsElectronMode(true)
    }

    if (isElectronMode) {
      this.$printTransactionsCommands.handlePrintDataComplete = (placedOrderItemIds, voidOrderItemIds) => {
        this.handlePrintDataComplete(placedOrderItemIds, voidOrderItemIds)
      }

      if (this.electronPrinterStatusInterval) clearInterval(this.electronPrinterStatusInterval)
      this.electronPrinterStatusInterval = setInterval(() => {
        this.updateElectronPrinterStatus()
      }, 1000)
    }

    const cookie = Cookies.get(this.$constants.cookies.kdsCookies)
    this.limit = cookie?.limit || this.limit
  },
  created () {
    _.extend(this, { isElectronMode })
  },
  beforeUnmount () {
    if (this.$printTransactionsCommands) this.$printTransactionsCommands.handlePrintDataComplete = null
    if (this.$remoteCommands) this.$remoteCommands.handleUpdatedKdsData = null

    if (!_.isNil(this.electronPrinterStatusInterval)) clearInterval(this.electronPrinterStatusInterval)
    if (!_.isNil(this.updateKdsDisplayInterval)) clearInterval(this.updateKdsDisplayInterval)
  },
  methods: {
    setThemeColors () {
      if (!this.currentSite) return

      this.$mh.setThemeColors(this.currentSite)
    },
    isOrderItemVisible (orderItem) {
      const shouldShowInCurrentTab = this.tab !== STAGES.COMPLETE ? orderItem.isUndelivered : !orderItem.isUndelivered
      const itemMatchesDeliveryFilter = this.deliveryTypesFilter ? orderItem.deliveryType === this.deliveryTypesFilter : true

      return shouldShowInCurrentTab && itemMatchesDeliveryFilter
    },
    setKitchenOrdersInterval () {
      const refreshOrdersIntervalSeconds = 15
      this.currentKdsUpdateIntervalStartedAt = null
      this.$remoteCommands.handleUpdatedKdsData = (orderId = null) => {
        this.refreshOrders(orderId)
      }

      if (!_.isNil(this.updateKdsDisplayInterval)) clearInterval(this.updateKdsDisplayInterval)

      this.updateKdsDisplayInterval = setInterval(async () => {
        // Skip fetching orders if a fetch is currently in progress. If the in-progress fetch has taken
        // longer than 2 interval cycles, force a refresh by allowing the 3rd interval cycle to fetch.
        const secondsUntilForcedRefresh = (refreshOrdersIntervalSeconds * 2) + 1
        const secondsSinceLastAttempt = this.currentKdsUpdateIntervalStartedAt ? ((new Date() - this.currentKdsUpdateIntervalStartedAt) / 1000) : 0
        if (!this.currentKdsUpdateIntervalStartedAt || secondsSinceLastAttempt > secondsUntilForcedRefresh) {
          if (secondsSinceLastAttempt > secondsUntilForcedRefresh) {
            this.$rollbar.warning(`KDS refreshOrders interval took over ${secondsUntilForcedRefresh} seconds (${secondsSinceLastAttempt}s), forcing refresh`)
          }

          this.currentKdsUpdateIntervalStartedAt = new Date()
          try {
            await this.refreshOrders()
          } catch (e) {
            this.$rollbar.error('KDS failed to refreshOrders in interval', e)
          } finally {
            this.currentKdsUpdateIntervalStartedAt = null
          }
        }
      }, refreshOrdersIntervalSeconds * 1000)
    },
    clearKitchenOrdersInterval () {
      this.$remoteCommands.handleUpdatedKdsData = null

      if (!_.isNil(this.updateKdsDisplayInterval)) {
        clearInterval(this.updateKdsDisplayInterval)
        this.updateKdsDisplayInterval = null
      }
    },
    // ELECTRON
    updateElectronPrinterStatus () {
      this.electronPrinterConnected = isElectronMode && !window.electronPrinter.status()?.error
    },
    async updateKdsClientIsElectronMode (isElectronMode) {
      this.currentKdsClient.isElectronMode = isElectronMode
      await this.currentKdsClient.save()
    },
    handlePrintDataComplete (placedOrderItemIds, voidOrderItemIds) {
      const staleOrderItems = _(this.kitchenOrders.data)
        .map('orderItems.data').flatten()
        .filter(orderItem => (!orderItem.pickPrepPrintedAt && placedOrderItemIds.includes(orderItem.id.toString())) ||
            (!orderItem.isVoided && voidOrderItemIds.includes(orderItem.id.toString())))
        .value()

      if (staleOrderItems.length) {
        this.refreshOrders()
      }
    },
    async setKitchenDisplay () {
      if (this.currentKdsClient) {
        this.$unsubscribeFromCableSubscriptions()
        this.currentKdsClient.delete()
        await this.$handleAuthChange()
      }

      this.showSetKitchenDisplayDialog = true
    },
    async refresh (orderId = null) {
      this.refreshOrders(orderId)

      // If we are refreshing a given order, we know an item has changed and we should refresh the summary
      // Otherwise we can rely on seeing a new order to refresh the summary
      if (orderId) this.refreshSummary()
    },
    async refreshSummary () {
      if (this.kitchenOrderSummary.options.variables.skip) return

      await this.kitchenOrderSummary.fetch(true)
    },
    async refreshOrders (orderId = null) {
      if (this.kitchenOrders.options.variables.skip) return

      let order
      if (orderId) {
        order = _.find(this.kitchenOrders.data, { id: Number(orderId) })
      }

      if (order) {
        order.options.variables = {
          id: order.id,
          kitchenDisplayId: this.kitchenDisplay.id,
          prepared: this.preparedFilterValue,
          delivered: this.deliveredFilterValue,
          locationIds: this.locationIdsFilterArray,
          deliveryTypes: this.deliveryTypesFilterArray,
          deliveryLocations: this.deliveryLocationsFilterArray
        }
        order.options.queryName = 'kitchenOrder'
        await order.fetch(true)
      } else {
        const startingOrderIds = _.map(this.kitchenOrders.data, 'id')
        await this.kitchenOrders.fetch(true)
        if (_.difference(_.map(this.kitchenOrders.data, 'id'), startingOrderIds).length > 0) {
          this.$piercingBeep()
          this.refreshSummary()
        }
      }
    }
  }
}
</script>
